import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableModule, TableLazyLoadEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { LocationService } from '../locations.service';
import { Location } from '../locations.model';
import { Page } from '../../../../models/page-wrapper.model';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { EntityDropdownPickerComponent } from '../../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { CustomersService } from '../../customers.service';
import { Customer, CustomerPickerSummary } from '../../customers.model';

@Component({
  selector: 'app-locations-list',
  templateUrl: './locations-list.component.html',
  styleUrl: './locations-list.component.css',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    ReactiveFormsModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    IftaLabelModule,
    EntityDropdownPickerComponent,
  ],
  providers: [ConfirmationService, MessageService],
})
export class LocationsListComponent implements OnInit {
  @ViewChild('dt') dt!: Table;
  locations: Location[] = [];
  totalRecords = 0;
  isLoading = false;
  filterForm!: FormGroup;
  private filterSub!: Subscription;
  customerPickerAdapter!: EntityDropdownPickerAdapter<CustomerPickerSummary, Customer>;

  constructor(
    private fb: FormBuilder,
    private locationService: LocationService,
    private customersService: CustomersService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.customerPickerAdapter = this.customersService.createCustomerPickerAdapter();
    this.buildFilterForm();
    this.filterSub = this.filterForm.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.dt.reset();
      });
  }

  buildFilterForm() {
      this.filterForm = this.fb.group({
      location: [''],
      locationNo: [''],
      siteArabicName: [''],
      address1: [''],
      customer: [null],
    });
  }

  onSearch(): void {
    this.dt.reset();
  }

  onReset(): void {
    this.filterForm.reset({
      location: '',
      locationNo: '',
      siteArabicName: '',
      address1: '',
      customer: null,
    });
    this.dt.reset();
  }

  loadLocations(event?: TableLazyLoadEvent) {
    this.isLoading = true;
    const size = event?.rows ?? 10;
    const page = event?.first ? Math.floor(event.first / size) : 0;


    const params: any = {
      page,
      size,
      location: this.filterForm.value.location,
      locationNo: this.filterForm.value.locationNo,
      customerId: this.filterForm.value.customer?.id,
      customerNumber: this.filterForm.value.customer?.customerNumber,
      siteArabicName: this.filterForm.value.siteArabicName,
      address1: this.filterForm.value.address1,
    };
    
    // Remove empty params
    Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key];
      }
    });

    
    this.locationService.getLocations(params).subscribe({
      next: (page: Page<Location>) => {
        this.locations = page.content || [];
        this.totalRecords = page.totalElements;
        this.isLoading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Locations units.',
        });
      },
    });
  }

  deleteLocation(location: Location): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${location.siteArabicName}?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.locationService.deleteLocation(location.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Location deleted successfully',
            });
            this.loadLocations();
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete Location.',
            });
          },
        });
      },
    });
  }
}
